import Table, { createColumns } from "@/v2-backoffice-client/components/table"
import {
  BORDER_WIDTH_OPTIONS,
  TABLE_CELL_PADDING_OPTIONS,
  TABLE_MULTI_SORT_OPTIONS,
  TABLE_STRIPED_OPTIONS,
  useBorderWidth,
  useTableCellPadding,
  useTableMultiSort,
  useTableStriped,
} from "@/v2-backoffice-client/settings/settings.config"
import SettingSection from "@/v2-backoffice-client/settings/SettingSection"
import SettingOptionGroup from "@/v2-backoffice-client/settings/SettingOptionGroup"

type PreviewRow = {
  Name: string
  Status: string
  Team: string
  Joined: string
}

const PREVIEW_COLUMNS = createColumns<PreviewRow>({
  enableSorting: false,
  enableHiding: false,
  columns: {
    Name: { id: "Name", label: "Name" },
    Status: { id: "Status", label: "Status" },
    Team: { id: "Team", label: "Team" },
    Joined: { id: "Joined", label: "Joined" },
  },
})

const PREVIEW_DATA: PreviewRow[] = [
  {
    Name: "John Smith",
    Status: "Active",
    Team: "Acme Corp",
    Joined: "Jan 15",
  },
  {
    Name: "Jane Doe",
    Status: "Pending",
    Team: "TechStart",
    Joined: "Jan 16",
  },
  {
    Name: "Bob Wilson",
    Status: "Active",
    Team: "Globex",
    Joined: "Jan 17",
  },
  {
    Name: "Alice Chen",
    Status: "Active",
    Team: "Acme Corp",
    Joined: "Jan 18",
  },
  {
    Name: "Mark Davis",
    Status: "Inactive",
    Team: "Initech",
    Joined: "Jan 19",
  },
]

function SettingTable() {
  const [borderWidth, setBorderWidth] = useBorderWidth()
  const [tableCellPadding, setTableCellPadding] = useTableCellPadding()
  const [tableStriped, setTableStriped] = useTableStriped()
  const [multiSort, setMultiSort] = useTableMultiSort()

  return (
    <SettingSection
      title="Table"
      description="Controls the visual density and style of data tables."
      preview={
        <Table
          className="rounded-md overflow-hidden"
          data={PREVIEW_DATA}
          columns={PREVIEW_COLUMNS}
          enableRowSelection={false}
          enableSorting={false}
        >
          <Table.Content />
        </Table>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Cell Padding
          </p>
          <SettingOptionGroup
            options={TABLE_CELL_PADDING_OPTIONS}
            value={tableCellPadding}
            onChange={setTableCellPadding}
          />
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Border Width
          </p>
          <SettingOptionGroup
            options={BORDER_WIDTH_OPTIONS}
            value={borderWidth}
            onChange={setBorderWidth}
          />
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Striped Rows
          </p>
          <SettingOptionGroup
            options={TABLE_STRIPED_OPTIONS}
            value={tableStriped}
            onChange={setTableStriped}
          />
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Multi-column Sorting
          </p>
          <p className="text-xs text-muted-foreground mb-2 max-w-prose">
            When on, each header click adds another tier to a prioritised sort
            (1st, 2nd, …). When off, a click simply re-sorts by that one column.
          </p>
          <SettingOptionGroup
            options={TABLE_MULTI_SORT_OPTIONS}
            value={multiSort}
            onChange={setMultiSort}
          />
        </div>
      </div>
    </SettingSection>
  )
}

export default SettingTable
